import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, CalendarIcon, MapPinIcon, ClockIcon, UsersIcon, SparklesIcon, MailCheckIcon, GlobeIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { GivebutterDonate } from '../components/GivebutterDonate';
const SANDOVAL_IMAGE = 'https://cdn.magicpatterns.com/uploads/41ina9BJwrzGYJTD1injLe/NeidaS.jpg';
const ZUNIGA_IMAGE = 'https://cdn.magicpatterns.com/uploads/sCwWAyRQF74f8hLN1q2Rrm/JoseZ.jpg';
const FLYER_IMAGE = 'https://cdn.magicpatterns.com/uploads/fdgKGqY2axfX3n3ihCbwUD/Voices_of_Impact_-_New_York.jpg';
const NY_BACKDROP = 'https://cdn.magicpatterns.com/patterns/generated-images/6004ba36-767f-4fce-adb9-f35b03fce46c.jpg';
const DETAIL_ICONS = [CalendarIcon, ClockIcon, MapPinIcon, UsersIcon];
const COPY = {
  EN: {
    back: 'Back to site',
    eyebrow: 'Hispanic Heritage Month · New York City',
    subtitle: 'A Hispanic Heritage Month Celebration',
    details: [{
      label: 'Date',
      value: 'Thursday, October 1'
    }, {
      label: 'Time',
      value: '5:00 – 8:00 PM'
    }, {
      label: 'Venue',
      value: '30 Rockefeller Plaza, New York, NY'
    }, {
      label: 'Format',
      value: 'Evening reception'
    }],
    flyerAria: 'View the full invitation graphic',
    flyerAlt: 'Voices of Impact — A Hispanic Heritage Month Celebration, Thursday, October 1, 30 Rockefeller Plaza, New York, 5–8 PM',
    rsvpTitle: 'Invited Guest RSVP',
    rsvpText: 'Guests who have received a formal invitation may confirm their attendance below.',
    guestsEyebrow: 'Special Guests',
    sandovalAlt: 'Voices of Impact special guest Neida Sandoval',
    zunigaAlt: 'Voices of Impact special guest José Zuñiga',
    guestsHeading: 'Joining Us That Evening',
    guestsIntro: "We are honored to welcome two distinguished Honduran voices whose work in journalism and the arts has carried our community's story to national audiences.",
    sandovalRole: 'Journalist & Broadcast Anchor',
    sandovalBio: 'A Honduran-born journalist with more than three decades in Spanish-language television, Neida Sandoval became one of the most recognized faces in U.S. Hispanic media as a longtime national morning-show anchor. Her reporting has taken her from natural disasters and immigration stories to interviews with heads of state, earning her Emmy recognition and a reputation for bringing Central American stories to audiences that rarely heard them.',
    zunigaRole: 'Actor, Film & Television',
    zunigaBioLead: 'Born in Honduras and raised in the United States, Jose Zuniga has built a decades-long career across film, television, and the New York stage. His film work includes ',
    zunigaBioMid: ', with television credits spanning ',
    zunigaBioEnd: '. His body of work has helped broaden how Latino characters are written and portrayed on screen.',
    aboutEyebrow: 'About the Evening',
    aboutHeading: 'A Room You Will Want to Be In',
    aboutParagraphs: ['Designed to create meaningful connections across industries and generations, Voices of Impact offers guests the opportunity to expand their networks, exchange ideas, discover new opportunities, and build relationships with individuals who are making an impact in their communities and beyond.', 'Throughout the evening, guests will also hear from distinguished special guests and inspiring voices whose leadership reflects the growing influence and contributions of the Honduran-American and broader Latino community in the United States.', 'Whether you are looking to meet fellow leaders, develop new partnerships, connect with emerging talent, or simply become part of a growing national network, Voices of Impact is a room you will want to be in.'],
    highlights: [{
      title: 'A room built for connection',
      text: 'Leaders, executives, entrepreneurs, professionals, public servants, cultural figures, and changemakers from across the country, gathered in one place.'
    }, {
      title: 'Voices worth hearing',
      text: 'Distinguished special guests and inspiring speakers whose leadership reflects the growing influence of the Honduran-American and broader Latino community.'
    }, {
      title: 'Across industries and generations',
      text: 'Expand your network, exchange ideas, discover opportunities, and meet emerging talent alongside established leaders.'
    }],
    missionHeading: 'Connect. Empower. Celebrate.',
    missionText: 'At the Honduran-American Association, our mission is to Connect, Empower, and Celebrate. This evening is about putting that mission into action — bringing remarkable people together and creating connections that can lead to new ideas, new collaborations, and lasting impact.',
    missionClosing: 'Come ready to connect. Leave with new relationships, new possibilities, and a stronger sense of what we can build together.',
    footer: 'Hosted by the Honduran-American Association'
  },
  ES: {
    back: 'Volver al sitio',
    eyebrow: 'Mes de la Herencia Hispana · Ciudad de Nueva York',
    subtitle: 'Una Celebración del Mes de la Herencia Hispana',
    details: [{
      label: 'Fecha',
      value: 'Jueves, 1 de octubre'
    }, {
      label: 'Hora',
      value: '5:00 – 8:00 P.M.'
    }, {
      label: 'Lugar',
      value: '30 Rockefeller Plaza, Nueva York, NY'
    }, {
      label: 'Formato',
      value: 'Recepción vespertina'
    }],
    flyerAria: 'Ver la invitación completa',
    flyerAlt: 'Voices of Impact — Una Celebración del Mes de la Herencia Hispana, jueves 1 de octubre, 30 Rockefeller Plaza, Nueva York, 5–8 P.M.',
    rsvpTitle: 'Confirmación de Invitados',
    rsvpText: 'Las personas que han recibido una invitación formal pueden confirmar su asistencia a continuación.',
    guestsEyebrow: 'Invitados Especiales',
    sandovalAlt: 'Invitada especial de Voices of Impact, Neida Sandoval',
    zunigaAlt: 'Invitado especial de Voices of Impact, José Zuñiga',
    guestsHeading: 'Nos Acompañan Esa Noche',
    guestsIntro: 'Nos honra dar la bienvenida a dos distinguidas voces hondureñas cuyo trabajo en el periodismo y las artes ha llevado la historia de nuestra comunidad a audiencias nacionales.',
    sandovalRole: 'Periodista y Presentadora',
    sandovalBio: 'Periodista nacida en Honduras con más de tres décadas en la televisión en español, Neida Sandoval se convirtió en uno de los rostros más reconocidos de los medios hispanos en Estados Unidos como presentadora de un programa matutino nacional durante muchos años. Su labor periodística la ha llevado desde desastres naturales e historias de inmigración hasta entrevistas con jefes de estado, obteniendo reconocimiento Emmy y la reputación de llevar las historias centroamericanas a audiencias que rara vez las escuchaban.',
    zunigaRole: 'Actor, Cine y Televisión',
    zunigaBioLead: 'Nacido en Honduras y criado en los Estados Unidos, Jose Zuniga ha construido una carrera de décadas en el cine, la televisión y los escenarios de Nueva York. Su trabajo en cine incluye ',
    zunigaBioMid: ', con créditos en televisión que abarcan ',
    zunigaBioEnd: '. Su trayectoria ha ayudado a ampliar la manera en que los personajes latinos son escritos y representados en pantalla.',
    aboutEyebrow: 'Sobre la Velada',
    aboutHeading: 'Una Sala en la Que Querrá Estar',
    aboutParagraphs: ['Diseñada para crear conexiones significativas entre industrias y generaciones, Voices of Impact ofrece a los invitados la oportunidad de ampliar sus redes, intercambiar ideas, descubrir nuevas oportunidades y construir relaciones con personas que están generando impacto en sus comunidades y más allá.', 'A lo largo de la velada, los invitados también escucharán a distinguidos invitados especiales y voces inspiradoras cuyo liderazgo refleja la creciente influencia y las contribuciones de la comunidad hondureño-americana y latina en los Estados Unidos.', 'Ya sea que busque conocer a otros líderes, desarrollar nuevas alianzas, conectar con talento emergente o simplemente formar parte de una red nacional en crecimiento, Voices of Impact es una sala en la que querrá estar.'],
    highlights: [{
      title: 'Una sala hecha para conectar',
      text: 'Líderes, ejecutivos, empresarios, profesionales, servidores públicos, figuras culturales y agentes de cambio de todo el país, reunidos en un mismo lugar.'
    }, {
      title: 'Voces que vale la pena escuchar',
      text: 'Invitados especiales distinguidos y oradores inspiradores cuyo liderazgo refleja la creciente influencia de la comunidad hondureño-americana y latina.'
    }, {
      title: 'Entre industrias y generaciones',
      text: 'Amplíe su red, intercambie ideas, descubra oportunidades y conozca talento emergente junto a líderes consolidados.'
    }],
    missionHeading: 'Conectar. Empoderar. Celebrar.',
    missionText: 'En la Asociación Hondureño-Americana, nuestra misión es Conectar, Empoderar y Celebrar. Esta velada es esa misión en acción: reunir a personas extraordinarias y crear vínculos que puedan dar paso a nuevas ideas, nuevas colaboraciones y un impacto duradero.',
    missionClosing: 'Venga listo para conectar. Váyase con nuevas relaciones, nuevas posibilidades y una idea más clara de lo que podemos construir juntos.',
    footer: 'Organizado por la Asociación Hondureño-Americana'
  }
};
const FILMS = ['Crooklyn', 'Smoke', 'Con Air', 'Alien Resurrection', 'Constantine'];
const SHOWS = ['Law & Order', 'NYPD Blue', 'ER', '24'];
function TitleList({
  titles
}: {
  titles: string[];
}) {
  return <>
      {titles.map((title, i) => <React.Fragment key={title} data-id="element-4023" data-mp="34co29r8we7xu" data-mpi={i}>
          <span className="text-white/90 italic" data-id="element-4024" data-mp="34cfrc4wc5cpa">{title}</span>
          {i < titles.length - 1 ? ', ' : ''}
        </React.Fragment>)}
    </>;
}
export function VoicesOfImpactNYPage() {
  const {
    language,
    setLanguage
  } = useLanguage();
  const isEs = language === 'ES';
  const c = isEs ? COPY.ES : COPY.EN;
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <div className="min-h-screen w-full bg-[#0b1f3f] text-white font-nunito" data-id="element-4025" data-mp="2ko6yrtqvr4p9">
      <header className="relative z-10 w-full px-6 py-6 sm:px-12 flex items-center justify-between gap-4 border-b border-[#c9a227]/30" data-id="element-4026" data-mp="2pnrvrymhp2zc">
        <a href="#" className="flex items-center gap-3 group" aria-label="Honduran American Association" data-id="element-4027" data-mp="2bp208nfbjbd3">
          <img src="https://cdn.magicpatterns.com/uploads/b1GB8Z9aEvoE58p9BdX4qx/HonduranAmericanLogoNew.png" alt="Honduran American Association" className="h-10 w-auto object-contain brightness-0 invert group-hover:opacity-80 transition-opacity" data-id="element-4028" data-mp="2gp7mtp6qe7ob" />
        </a>
        <div className="flex items-center gap-3" data-id="element-4029" data-mp="oxatzhoxrj4p">
          <button onClick={() => setLanguage(isEs ? 'EN' : 'ES')} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#c9a227] hover:bg-[#dcb63b] text-[#0b1f3f] text-sm font-bold transition-colors" aria-label={isEs ? 'Switch to English' : 'Ver en español'} data-id="element-4030" data-mp="k1n5l4ia2u09">
            <GlobeIcon className="w-4 h-4" data-id="element-4031" data-mp="17obs63i8fan7" />
            {isEs ? 'English' : 'Español'}
          </button>
          <a href="#" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 text-sm font-semibold transition-colors" data-id="element-4032" data-mp="12qtpguut5rnn">
            <ArrowLeftIcon className="w-4 h-4" data-id="element-4033" data-mp="1cnf0bwtidwze" />
            <span className="hidden sm:inline" data-id="element-4034" data-mp="3ij97361vrxhy">{c.back}</span>
          </a>
        </div>
      </header>

      <main data-id="element-4035" data-mp="3ehxr7s8t6qmy">
        {/* Invitation */}
        <section className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 pt-12 lg:pt-16" data-id="element-4036" data-mp="3eiz29wptrd4o">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          ease: [0.16, 1, 0.3, 1]
        }} className="text-center max-w-3xl mx-auto" data-id="element-4037" data-mp="1hi2wz6l9bjwz">
            <p className="font-nunito text-[#c9a227] text-xs font-bold uppercase tracking-[0.25em] mb-4" data-id="element-4038" data-mp="20e2v6z4tvjp0">
              {c.eyebrow}
            </p>
            <h1 className="font-bebas text-5xl sm:text-6xl lg:text-7xl tracking-[0.08em] leading-[0.95] mb-3" data-id="element-4039" data-mp="2a4nny5xtlu00">
              Voices of Impact
            </h1>
            <p className="font-nunito text-white/80 text-sm uppercase tracking-[0.2em]" data-id="element-4040" data-mp="2f37g4o30c20t">
              {c.subtitle}
            </p>
            <div className="w-20 h-px bg-[#c9a227] mx-auto mt-7" data-id="element-4041" data-mp="2k10l5dskqpzi" />
          </motion.div>

          {/* Info bar */}
          <dl className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-px rounded-2xl overflow-hidden border border-white/10 bg-white/10" data-id="element-4042" data-mp="cgtqk88f8jj1">
            {c.details.map((item, i) => {
            const Icon = DETAIL_ICONS[i];
            return <div key={item.label} className="bg-[#0b1f3f] px-5 py-5 flex items-start gap-3" data-id="element-4043" data-mp="3gx3gb1ojsq3" data-mpi={i}>
                  <Icon className="w-4 h-4 text-[#c9a227] mt-0.5 flex-shrink-0" data-id="element-4044" data-mp="w4swn5s1j2i4" />
                  <div data-id="element-4045" data-mp="r5zl3iwbdlkl">
                    <dt className="font-nunito text-white/45 text-[10px] font-bold uppercase tracking-[0.18em] mb-0.5" data-id="element-4046" data-mp="r5g4c5ieh385">
                      {item.label}
                    </dt>
                    <dd className="font-nunito text-white text-sm font-semibold leading-snug" data-id="element-4047" data-mp="hg6cmqrgxmkk">
                      {item.value}
                    </dd>
                  </div>
                </div>;
          })}
          </dl>
        </section>

        <section className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 py-12 lg:py-16" data-id="element-4048" data-mp="1a3926te62myo">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-12 items-stretch" data-id="element-4049" data-mp="ul98l6424d2k">
            {/* Left — flyer */}
            <motion.aside initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            ease: [0.16, 1, 0.3, 1]
          }} className="flex" data-id="element-4050" data-mp="ypqmwprndy5y">
              <a href={FLYER_IMAGE} target="_blank" rel="noopener noreferrer" className="relative block w-full h-full rounded-2xl overflow-hidden shadow-2xl ring-1 ring-[#c9a227]/50 bg-white group" aria-label={c.flyerAria} data-id="element-4051" data-mp="1ib3jvj8q9tkp">
                <img src={FLYER_IMAGE} alt={c.flyerAlt} className="w-full h-full object-contain block transition-transform duration-500 group-hover:scale-[1.02]" data-id="element-4052" data-mp="1wav5o65hke99" />
              </a>
            </motion.aside>

            {/* Right — RSVP */}
            <motion.div initial={{
            opacity: 0,
            y: 24
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.1,
            ease: [0.16, 1, 0.3, 1]
          }} className="flex flex-col h-full" data-id="element-4053" data-mp="3gs49vj6ugxny">
              <div className="rounded-2xl border border-[#c9a227]/40 bg-white/[0.04] overflow-hidden flex flex-col flex-1" data-id="element-4054" data-mp="3bv4316267qvs">
                <div className="px-6 pt-6 pb-4" data-id="element-4055" data-mp="36vj24q3fx8gf">
                  <h2 className="inline-flex items-center gap-2 font-bebas text-2xl tracking-wider mb-1.5" data-id="element-4056" data-mp="36y5bmv9w5y1a">
                    <MailCheckIcon className="w-5 h-5 text-[#c9a227]" data-id="element-4057" data-mp="r6ptwyeh3mwu" />
                    {c.rsvpTitle}
                  </h2>
                  <p className="font-nunito text-white/65 text-sm leading-relaxed" data-id="element-4058" data-mp="3fy1pqn8s38gi">
                    {c.rsvpText}
                  </p>
                </div>
                <div className="bg-white p-4 sm:p-5 flex-1 flex flex-col justify-center" data-id="element-4059" data-mp="3kyz9v5dcmckn">
                  <GivebutterDonate data-id="element-4060" data-mp="3oz9dwchkyswv" />
                  {React.createElement('givebutter-widget', {
                  id: 'LylV7o'
                })}
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Special guests */}
        <section className="border-t border-white/10" data-id="element-4061" data-mp="319ovpvt93zon">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 py-16 lg:py-20" data-id="element-4062" data-mp="3688ghsw3z5ff">
            <div className="text-center max-w-3xl mx-auto mb-12" data-id="element-4063" data-mp="3665kms89yi2s">
              <p className="inline-flex items-center gap-2 font-nunito text-[#c9a227] text-xs font-bold uppercase tracking-[0.25em] mb-5" data-id="element-4064" data-mp="3b09ov3q5oc06">
                <SparklesIcon className="w-4 h-4" data-id="element-4065" data-mp="2iie39a7vneun" />
                {c.guestsEyebrow}
              </p>
              <h2 className="font-bebas text-4xl sm:text-5xl tracking-wider leading-tight mb-4" data-id="element-4066" data-mp="2ayms64txw9qa">
                {c.guestsHeading}
              </h2>
              <div className="w-16 h-px bg-[#c9a227] mx-auto mb-7" data-id="element-4067" data-mp="260bipv7k418l" />
              <p className="font-nunito text-white/80 text-base sm:text-lg leading-relaxed" data-id="element-4068" data-mp="20zxl773l09ot">
                {c.guestsIntro}
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-10 lg:gap-12" data-id="element-4069" data-mp="1x1g9ea97ub12">
              {[{
              name: 'Neida Sandoval',
              image: SANDOVAL_IMAGE,
              alt: c.sandovalAlt,
              role: c.sandovalRole,
              bio: <>{c.sandovalBio}</>
            }, {
              name: 'José Zuñiga',
              image: ZUNIGA_IMAGE,
              alt: c.zunigaAlt,
              role: c.zunigaRole,
              bio: <>
                      {c.zunigaBioLead}
                      <TitleList titles={FILMS} data-id="element-4070" data-mp="14cskm4p9d3ir" />
                      {c.zunigaBioMid}
                      <TitleList titles={SHOWS} data-id="element-4071" data-mp="2s51i11wfq7ma" />
                      {c.zunigaBioEnd}
                    </>
            }].map((guest, i) => <motion.article key={guest.name} initial={{
              opacity: 0,
              y: 24
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-80px'
            }} transition={{
              duration: 0.5,
              delay: i * 0.1,
              ease: [0.16, 1, 0.3, 1]
            }} className="flex flex-col" data-id="element-4072" data-mp="2j5wiptd1ua92" data-mpi={i}>
                  <div className="rounded-2xl overflow-hidden border border-[#c9a227]/40 shadow-2xl bg-white" data-id="element-4073" data-mp="1vgvhaq2mvzda">
                    <img src={guest.image} alt={guest.alt} className="w-full h-auto object-contain block" data-id="element-4074" data-mp="1butqnrah9syi" />
                  </div>
                  <div className="pt-7" data-id="element-4075" data-mp="1kw057utkgtp4">
                    <h3 className="font-bebas text-3xl tracking-wider mb-1.5" data-id="element-4076" data-mp="1kx2ujyb8qo2x">
                      {guest.name}
                    </h3>
                    <p className="font-nunito text-[#c9a227] text-[11px] font-bold uppercase tracking-[0.18em] mb-4" data-id="element-4077" data-mp="1uto0clv3437t">
                      {guest.role}
                    </p>
                    <p className="font-nunito text-white/70 text-sm leading-relaxed" data-id="element-4078" data-mp="1pt0ba5qf1wg8">
                      {guest.bio}
                    </p>
                  </div>
                </motion.article>)}
            </div>

          </div>
        </section>

        {/* About the evening — New York backdrop */}
        <section className="relative border-t border-white/10 overflow-hidden" data-id="element-4079" data-mp="28lm2gcqdoyuh">
          <img src={NY_BACKDROP} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover" data-id="element-4080" data-mp="2l42fr31e2eb3" />
          <div className="absolute inset-0 bg-[#0b1f3f]/55" data-id="element-4081" data-mp="2h27ic9mkwl2l" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0b1f3f] via-[#0b1f3f]/20 to-[#0b1f3f]" data-id="element-4082" data-mp="2v2i5b1krpfih" />

          <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-12 py-20 lg:py-24" data-id="element-4083" data-mp="2q46vgquh3509">
            <div className="text-center max-w-3xl mx-auto" data-id="element-4084" data-mp="22emegdhvqhtm">
              <p className="inline-flex items-center gap-2 font-nunito text-[#c9a227] text-xs font-bold uppercase tracking-[0.25em] mb-5" data-id="element-4085" data-mp="1xehxkmd27l2v">
                <SparklesIcon className="w-4 h-4" data-id="element-4086" data-mp="2c4fmx5mq6gtn" />
                {c.aboutEyebrow}
              </p>
              <h2 className="font-bebas text-4xl sm:text-5xl tracking-wider leading-tight mb-4" data-id="element-4087" data-mp="27bmhsgnch6h1">
                {c.aboutHeading}
              </h2>
              <div className="w-16 h-px bg-[#c9a227] mx-auto mb-8" data-id="element-4088" data-mp="1jkqo1dzbw175" />
            </div>
            <div className="space-y-5 font-nunito text-white/80 text-base sm:text-lg leading-relaxed text-center max-w-3xl mx-auto" data-id="element-4089" data-mp="1elw3a6hsw2ke">
              {c.aboutParagraphs.map(text => <p key={text} data-id="element-4090" data-mp="3v3d94xerty5s">{text}</p>)}
            </div>

            <div className="grid md:grid-cols-3 gap-6 mt-14" data-id="element-4091" data-mp="3hboykyk67hqx">
              {c.highlights.map((item, i) => <motion.div key={item.title} initial={{
              opacity: 0,
              y: 24
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-60px'
            }} transition={{
              duration: 0.5,
              delay: i * 0.1,
              ease: [0.16, 1, 0.3, 1]
            }} className="rounded-2xl border border-white/15 bg-[#0b1f3f]/70 backdrop-blur-sm p-7 text-center" data-id="element-4092" data-mp="3lcsefl27xds5" data-mpi={i}>
                  <div className="w-10 h-px bg-[#c9a227] mb-5 mx-auto" data-id="element-4093" data-mp="z4cuafmhayoq" />
                  <h3 className="font-bebas text-2xl tracking-wider mb-3" data-id="element-4094" data-mp="u6t36f4omrfd">
                    {item.title}
                  </h3>
                  <p className="font-nunito text-white/70 text-sm leading-relaxed" data-id="element-4095" data-mp="pd8exesw5btd">
                    {item.text}
                  </p>
                </motion.div>)}
            </div>
          </div>
        </section>

        {/* Mission */}
        <section className="border-t border-white/10 bg-white/[0.03]" data-id="element-4096" data-mp="bf0lvhyz6unh">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16 text-center" data-id="element-4097" data-mp="6g777m7ycxlq">
            <h2 className="font-bebas text-3xl sm:text-4xl tracking-wider leading-tight mb-5" data-id="element-4098" data-mp="6i9va0pjognt">
              {c.missionHeading}
            </h2>
            <p className="font-nunito text-white/75 text-base sm:text-lg leading-relaxed mb-4" data-id="element-4099" data-mp="20uq6lfx76kzj">
              {c.missionText}
            </p>
            <p className="font-nunito text-white text-base sm:text-lg font-semibold leading-relaxed" data-id="element-4100" data-mp="1wteg786i1vqa">
              {c.missionClosing}
            </p>
          </div>
        </section>
      </main>

      <footer className="border-t border-white/10 py-8 text-center" data-id="element-4101" data-mp="3jnroxz4x1da1">
        <p className="font-nunito text-white/45 text-xs uppercase tracking-[0.2em]" data-id="element-4102" data-mp="3oft2rphqfc26">
          {c.footer}
        </p>
      </footer>
    </div>;
}